import { featureCollection } from '@turf/helpers'
import { GEOJSON_COORDINATE_PRECISION } from 'api-validations/geojson-schemas'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import OpenlayersFeature from 'ol/Feature'
import { GeoJSON as OpenlayersGeoJSONFormatter } from 'ol/format'
import type { WriteOptions } from 'ol/format/Feature'
import type { LineString as OpenLayersLineString, Polygon as OpenlayersPolygon } from 'ol/geom'
import type OpenlayersGeometry from 'ol/geom/Geometry'

import { isNil, isNonNil } from '../object'

const formatOptions: WriteOptions = {
  featureProjection: 'EPSG:3857',
  dataProjection: 'EPSG:4326',
  rightHanded: true,
  decimals: GEOJSON_COORDINATE_PRECISION
}

/**
 * We assume that we're always working with EPSG:3857 for rendering
 * and EPSG:4326 for data, so we set the format accordingly.
 */
const format = new OpenlayersGeoJSONFormatter(formatOptions)

/**
 * Utilities for converting between GeoJSON and OpenLayers features/geometries.
 */
export const GeoJSON = {
  /**
   * Converts an OpenLayers feature to a GeoJSON feature.
   */
  fromOpenlayersFeature: <TGeometry extends Geometry>(
    feat: OpenlayersFeature,
    test?: (g: Geometry) => g is TGeometry
  ) => {
    const data = format.writeFeatureObject(feat, formatOptions)
    if (isNil(test) || test(data.geometry)) {
      return data as Feature<TGeometry>
    }
    if (test(data.geometry)) {
      return data as Feature<TGeometry>
    }
    return undefined
  },

  /**
   * Converts OpenLayers features to a GeoJSON feature collection.
   */
  fromOpenlayersFeatures: <TGeometry extends Geometry>(
    features: OpenlayersFeature<OpenlayersGeometry>[],
    test?: (g: Geometry) => g is TGeometry
  ): FeatureCollection<TGeometry> => {
    const hasTest = isNonNil(test)
    return featureCollection(
      features.flatMap((f) => {
        const data = format.writeFeatureObject(f, formatOptions)
        return !hasTest || test(data.geometry) ? data : []
      })
    ) as FeatureCollection<TGeometry>
  },

  /**
   * Converts a GeoJSON feature collection to OpenLayers features.
   */
  toOpenlayersFeatures: (
    featureCollection: FeatureCollection
  ): OpenlayersFeature<OpenlayersGeometry>[] => {
    return format.readFeatures(featureCollection, formatOptions)
  },

  /**
   * Converts a GeoJSON geometry to an OpenLayers geometry.
   */
  toOpenlayersGeometry: (geometry: Geometry): OpenlayersGeometry => {
    const olGeometry = format.readGeometry(geometry, formatOptions)
    if (olGeometry === null) {
      throw new Error(`OpenLayers could not read a ${geometry.type} geometry`)
    }
    return olGeometry
  }
}

/**
 * Utilities for working with OpenLayers features and geometries.
 */
export const Openlayers = {
  /**
   * Type guard to check if an OpenLayers feature is a polygon.
   */
  isPolygon: (
    feature: OpenlayersFeature<OpenlayersGeometry> | undefined | null
  ): feature is OpenlayersFeature<OpenlayersPolygon> => {
    return feature instanceof OpenlayersFeature && feature.getGeometry()?.getType() === 'Polygon'
  },

  /**
   * Type guard to check if an OpenLayers feature is a polygon.
   */
  isLineString: (
    feature: OpenlayersFeature<OpenlayersGeometry> | undefined | null
  ): feature is OpenlayersFeature<OpenLayersLineString> => {
    return feature instanceof OpenlayersFeature && feature.getGeometry()?.getType() === 'LineString'
  },

  /**
   * Converts OpenLayers features to a GeoJSON string.
   */
  toGeoJSONString: (features: OpenlayersFeature<OpenlayersGeometry>[]): string => {
    return format.writeFeatures(features, formatOptions)
  }
}
